import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding, ReadFileResult } from '@capacitor/filesystem';
import { Preferences } from '@capacitor/preferences';
import { cardImageCache } from './card-image-cache';
import { USE_LOCAL_ASSETS } from '@/config/constants';

// Type definitions for deck management
interface DeckManifest {
  version: number;
  lastUpdated: string;
  decks: DeckInfo[];
}

interface DeckInfo {
  id: number;
  name: string;
  deckUrl: string;
  membership: string;
  isDownloaded: boolean;
  cardFiles: string[];
}

class DeckStorageService {
  private static MANIFEST_KEY = 'deck_manifest';
  private static FIRST_LAUNCH_KEY = 'first_launch_completed';
  private static DECK_DIRECTORY = 'decks';
  
  private manifest: DeckManifest | null = null;
  private isInitialized = false;
  private downloadQueue: { deckId: number, onProgress?: (progress: number) => void }[] = [];
  private isDownloading = false;
  private concurrentDownloads = 3;

  constructor() {
    this.initialize();
  }

  /**
   * Initialize the deck storage service
   */
  async initialize(): Promise<void> {
    if (this.isInitialized || !this.isNativePlatform()) return;

    try {
      // Create base directory if it doesn't exist
      await this.ensureDirectory(DeckStorageService.DECK_DIRECTORY);
      
      // Load existing manifest
      await this.loadManifest();
      
      this.isInitialized = true;
      
      // Check if it's first launch
      await this.checkFirstLaunch();
    } catch (error) {
      console.error('Failed to initialize DeckStorageService:', error);
    }
  }

  /**
   * Check if this is the first app launch and trigger initial deck download
   */
  private async checkFirstLaunch(): Promise<void> {
    try {
      const { value } = await Preferences.get({ key: DeckStorageService.FIRST_LAUNCH_KEY });
      
      if (value !== 'true') {
        console.log('First launch detected, will download default decks');
        // Mark first launch as completed immediately to prevent repeated attempts
        await Preferences.set({ key: DeckStorageService.FIRST_LAUNCH_KEY, value: 'true' });
        // Start downloading all decks in the background
        this.downloadAllDecks();
      }
    } catch (error) {
      console.error('Error checking first launch:', error);
    }
  }

  /**
   * Load the deck manifest from preferences
   */
  private async loadManifest(): Promise<void> {
    try {
      const { value } = await Preferences.get({ key: DeckStorageService.MANIFEST_KEY });
      
      if (value) {
        this.manifest = JSON.parse(value);
      } else {
        // Initialize empty manifest
        this.manifest = {
          version: 1,
          lastUpdated: new Date().toISOString(),
          decks: []
        };
        await this.saveManifest();
      }
    } catch (error) {
      console.error('Failed to load deck manifest:', error);
      // Initialize with empty manifest on error
      this.manifest = {
        version: 1,
        lastUpdated: new Date().toISOString(),
        decks: []
      };
      await this.saveManifest();
    }
  }

  /**
   * Save the current manifest to preferences
   */
  private async saveManifest(): Promise<void> {
    if (!this.manifest) return;
    
    try {
      await Preferences.set({
        key: DeckStorageService.MANIFEST_KEY,
        value: JSON.stringify(this.manifest)
      });
    } catch (error) {
      console.error('Failed to save deck manifest:', error);
    }
  }
  
  /**
   * Ensure a directory exists, creating it if necessary
   */
  private async ensureDirectory(path: string): Promise<void> {
    try {
      await Filesystem.mkdir({
        path,
        directory: Directory.Data,
        recursive: true
      });
    } catch (error: any) {
      // Ignore error if directory already exists
      if (error.message && !error.message.includes('exists')) {
        throw error;
      }
    }
  }

  /**
   * Update the deck manifest with latest decks from API
   */
  async updateDeckManifest(decks: any[]): Promise<void> {
    if (!this.isNativePlatform() || !this.manifest) return;
    
    // Update existing decks and add new ones
    for (const deck of decks) {
      const existingDeckIndex = this.manifest.decks.findIndex(d => d.id === deck.id);
      
      if (existingDeckIndex >= 0) {
        // Update existing deck info but preserve downloaded status
        const isDownloaded = this.manifest.decks[existingDeckIndex].isDownloaded;
        this.manifest.decks[existingDeckIndex] = {
          ...deck,
          isDownloaded,
          cardFiles: this.generateCardFileList(deck.deckType || 'standard')
        };
      } else {
        // Add new deck
        this.manifest.decks.push({
          ...deck,
          isDownloaded: false,
          cardFiles: this.generateCardFileList(deck.deckType || 'standard')
        });
      }
    }
    
    this.manifest.lastUpdated = new Date().toISOString();
    await this.saveManifest();
  }
  
  /**
   * Generate a list of card files for a deck based on its type
   */
  private generateCardFileList(deckType: string): string[] {
    const cardFiles: string[] = [];
    const suits = ['A', 'B', 'C', 'D']; // Spades, Hearts, Clubs, Diamonds
    
    // Add card back
    cardFiles.push('back.svg');
    
    // Standard deck: 6-King for all suits
    const startRank = 6;
    const endRank = deckType === 'full' ? 14 : 13; // Include Ace (14) for full decks
    
    for (const suit of suits) {
      for (let rank = startRank; rank <= endRank; rank++) {
        cardFiles.push(`${suit}${rank}.svg`);
      }
    }
    
    // Add jokers for full decks
    if (deckType === 'full') {
      cardFiles.push('E0.svg'); // Black joker
      cardFiles.push('F0.svg'); // Red joker
    }
    
    return cardFiles;
  }

  /**
   * Download all decks in the manifest
   */
  async downloadAllDecks(): Promise<void> {
    if (!this.isNativePlatform() || !this.manifest) return;
    
    // Queue all decks for download
    for (const deck of this.manifest.decks) {
      if (!deck.isDownloaded) {
        this.queueDeckDownload(deck.id);
      }
    }
    
    // Start processing queue if not already downloading
    this.processDownloadQueue();
  }
  
  /**
   * Check if a deck is already downloaded
   */
  async isDeckDownloaded(deckId: number): Promise<boolean> {
    if (!this.isNativePlatform() || !this.manifest) return false;
    
    // Ensure the manifest is loaded
    if (!this.isInitialized) {
      await this.initialize();
    }
    
    // Find the deck in the manifest
    const deckIndex = this.manifest.decks.findIndex(d => d.id === deckId);
    if (deckIndex >= 0) {
      return this.manifest.decks[deckIndex].isDownloaded;
    }
    
    return false;
  }
  
  /**
   * Queue a deck for download if not already downloaded
   * @param deckId The ID of the deck to download
   * @param onProgress Optional callback for download progress
   * @param forceDownload When true, delete existing files and redownload
   * @returns A promise that resolves to a boolean indicating if download was queued
   */
  async queueDeckDownloadIfNeeded(
    deckId: number, 
    onProgress?: (progress: number) => void, 
    forceDownload: boolean = false
  ): Promise<boolean> {
    if (!this.isNativePlatform()) return false;
    
    // Check if the deck is already downloaded
    const isAlreadyDownloaded = await this.isDeckDownloaded(deckId);
    
    // If forceDownload is true, delete existing deck files first
    if (isAlreadyDownloaded && forceDownload) {
      console.log(`Force redownloading deck ${deckId}, deleting existing files first`);
      await this.deleteDeck(deckId);
      // Queue the download
      this.queueDeckDownload(deckId, onProgress);
      return true; // Download was queued
    }
    
    // Normal flow for non-force download
    if (isAlreadyDownloaded && !forceDownload) {
      // If already downloaded, call the progress callback with 100%
      if (onProgress) {
        onProgress(100);
      }
      return false; // No download was queued
    }
    
    // Queue the download since it's needed
    this.queueDeckDownload(deckId, onProgress);
    return true; // Download was queued
  }

  /**
   * Queue a deck for download
   */
  queueDeckDownload(deckId: number, onProgress?: (progress: number) => void): void {
    if (!this.isNativePlatform()) return;
    
    this.downloadQueue.push({ deckId, onProgress });
    
    // Start processing queue if not already downloading
    if (!this.isDownloading) {
      this.processDownloadQueue();
    }
  }
  
  /**
   * Process the download queue
   */
  private async processDownloadQueue(): Promise<void> {
    if (this.isDownloading || this.downloadQueue.length === 0 || !this.manifest) return;
    
    this.isDownloading = true;
    
    try {
      while (this.downloadQueue.length > 0) {
        // Take up to concurrentDownloads items from queue
        const batch = this.downloadQueue.splice(0, this.concurrentDownloads);
        
        // Process batch concurrently
        await Promise.all(batch.map(async (item) => {
          const deck = this.manifest!.decks.find(d => d.id === item.deckId);
          if (deck) {
            await this.downloadDeck(deck, item.onProgress);
          }
        }));
      }
    } catch (error) {
      console.error('Error processing download queue:', error);
    } finally {
      this.isDownloading = false;
    }
  }
  
  /**
   * Download a specific deck
   */
  private async downloadDeck(deck: DeckInfo, onProgress?: (progress: number) => void): Promise<void> {
    // Skip download when using local assets
    if (USE_LOCAL_ASSETS) {
      console.log('Skipping deck download - using local assets');
      return;
    }
    
    if (!this.isNativePlatform()) return;
    
    try {
      // Create directory for this deck
      const deckPath = `${DeckStorageService.DECK_DIRECTORY}/${deck.id}`;
      await this.ensureDirectory(deckPath);
      
      const totalFiles = deck.cardFiles.length;
      let downloadedFiles = 0;
      
      // Download each card file
      for (const cardFile of deck.cardFiles) {
        try {
          const remoteUrl = `${deck.deckUrl}/${cardFile}`;
          const localPath = `${deckPath}/${cardFile}`;
          
          // Download file
          const response = await fetch(remoteUrl);
          const blob = await response.blob();
          
          // Convert blob to base64
          const base64Data = await this.blobToBase64(blob);
          
          // Write to filesystem
          await Filesystem.writeFile({
            path: localPath,
            data: base64Data,
            directory: Directory.Data,
            recursive: true
          });
          
          downloadedFiles++;
          
          // Report progress
          if (onProgress) {
            onProgress(Math.round((downloadedFiles / totalFiles) * 100));
          }
        } catch (fileError) {
          console.warn(`Failed to download card file ${cardFile} for deck ${deck.id}:`, fileError);
        }
      }
      
      // Mark deck as downloaded in manifest
      const deckIndex = this.manifest!.decks.findIndex(d => d.id === deck.id);
      if (deckIndex >= 0) {
        this.manifest!.decks[deckIndex].isDownloaded = true;
        await this.saveManifest();
      }
      
      console.log(`Completed download of deck ${deck.id} (${deck.name})`);
    } catch (error) {
      console.error(`Failed to download deck ${deck.id}:`, error);
    }
  }

  /**
   * Convert a Blob to base64 string
   */
  private blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        // Remove data URL prefix (e.g., "data:image/svg+xml;base64,")
        const base64 = base64String.split(',')[1];
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed to convert blob to base64'));
      reader.readAsDataURL(blob);
    });
  }
  
  /**
   * Get the local URL for a card image
   */
  async getLocalImageUrl(remoteUrl: string): Promise<string | null> {
    if (!this.isNativePlatform() || !this.manifest) return null;
    
    try {
      // Parse deck ID and card name from remote URL
      // Expected format: [baseUrl]/[deckId]/[cardName].svg
      const urlParts = remoteUrl.split('/');
      const cardName = urlParts[urlParts.length - 1];
      
      // Find the deck that matches this URL
      const deck = this.manifest.decks.find(d => remoteUrl.startsWith(d.deckUrl));
      
      if (!deck || !deck.isDownloaded) return null;
      
      const localPath = `${DeckStorageService.DECK_DIRECTORY}/${deck.id}/${cardName}`;
      
      // Check if file exists
      try {
        const fileInfo = await Filesystem.stat({
          path: localPath,
          directory: Directory.Data
        });
        
        if (fileInfo) {
          // Return the local file URL
          const result = await Filesystem.getUri({
            path: localPath,
            directory: Directory.Data
          });
          
          return result.uri;
        }
      } catch {
        // File doesn't exist locally
        return null;
      }
    } catch (error) {
      console.warn('Error getting local image URL:', error);
      return null;
    }
  }
  
  /**
   * Check if we're running on a native platform
   */
  isNativePlatform(): boolean {
    return Capacitor.isNativePlatform();
  }
  
  /**
   * Delete a downloaded deck
   */
  async deleteDeck(deckId: number): Promise<void> {
    if (!this.isNativePlatform() || !this.manifest) return;
    
    try {
      const deckPath = `${DeckStorageService.DECK_DIRECTORY}/${deckId}`;
      
      await Filesystem.rmdir({
        path: deckPath,
        directory: Directory.Data,
        recursive: true
      });
      
      // Update manifest
      const deckIndex = this.manifest.decks.findIndex(d => d.id === deckId);
      if (deckIndex >= 0) {
        this.manifest.decks[deckIndex].isDownloaded = false;
        await this.saveManifest();
      }
    } catch (error) {
      console.error(`Failed to delete deck ${deckId}:`, error);
    }
  }
}

// Create singleton instance
export const deckStorageService = new DeckStorageService();